import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import first from "@ckeditor/ckeditor5-utils/src/first";
import { CkeditorConstants, checkSelectedLink } from "../../src/utils";
import randomUUIDGenerator from "../../src/utils/randomUUIDGenerator";

const {
  class: { WK_KEYWORD_REF },
  htmlElement: { SPAN, PARAGRAPH, HEADER, LIST_ITEM, DIV, FIGURETAG, LINK_SPAN },
  htmlAttributes: { REL, PROPERTY, CLASS, LIST_STYLE },
  helperAttributes: { SUBTEMPLATE_NAME, NUMBERED_CITATION, TRACK_CHANGES_EDITING, DATA_FRAGMENT },
  suggestionType: { FORMAT_INLINE, INSERTION, DELETION },
  trackchangesMarkup: { SUGGESTION },
  HEADING_TAG_NAMES,
  DCTERMS_TITLE,
  htmlTagNames: { P },
  event: { SHOW_MESSAGE, BACKSPACE, DELETE },
  localStorageItem: { RHP_CITATION_REF_CLICK },
  REMOVE,
  INSERT,
  PLUGINS: { RELEASE_NOTES },
  event: { MOUSEUP }
} = CkeditorConstants;

export default class CustomEventListeners extends Plugin {
  init() {
    const editor = this.editor;
    /**
     * Event listener after 'Enter' event is triggered
     */
    editor.commands.get("enter").on(
      "execute",
      (evt) => {
        const rhpCitationRefClicked = JSON.parse(localStorage.getItem(RHP_CITATION_REF_CLICK));
        if (rhpCitationRefClicked) {
          editor.execute('shiftEnter');
          evt.stop();
        }
        let isViewSelectionHasSpan = null;
        let spanWithoutDataSubtemplateName = null;
        /**
         * Checks if user has pressed enter key inside inline subtemplate
         */
        if (
          this.editor.editing.view.document &&
          this.editor.editing.view.document.selection &&
          this.editor.editing.view.document.selection.anchor
        ) {
          const ancestors = this.getReverseAncestors(this.editor);
          if (this.checkParentElementHeader(ancestors, 0, true)) {
            return this.stopEventAndShowMessage(evt);
          }
          isViewSelectionHasSpan = ancestors.find((item) => {
            if (item.name === SPAN && item.hasAttribute(SUBTEMPLATE_NAME)) {
              return item.parent;
            }
          });

          spanWithoutDataSubtemplateName = ancestors.find(
            (item) =>
              item.name === SPAN &&
              !item.hasAttribute(SUBTEMPLATE_NAME) &&
              !item.hasAttribute(SUGGESTION) &&
              !item.hasAttribute(REL)
          );
        }

        /**
         * Checks if enter key action is inside inline subtemplate with p as parent
         */
        if (
          isViewSelectionHasSpan &&
          !isViewSelectionHasSpan.parent.hasAttribute(LIST_STYLE)
        ) {
          evt.stop();
          this.inlineSubTempOnP(isViewSelectionHasSpan, editor);
        } else if (
          isViewSelectionHasSpan &&
          isViewSelectionHasSpan.parent.hasAttribute(LIST_STYLE)
        ) {
          /**
           * Checks if enter key action is inside inline subtemplate with p as parent
           */
          evt.stop();
          this.inlineSubTempOnLi(isViewSelectionHasSpan, editor);
        } else if (
          spanWithoutDataSubtemplateName &&
          this.editor.model.document.selection.anchor &&
          this.editor.model.document.selection.anchor.parent &&
          this.editor.model.document.selection.anchor.parent.name !== PARAGRAPH) {
          /**
          * If enter key event on span tags without span data-subtemplate-name as parent
          *
          * @param editor
          * @param evt
          */
          this.spanTagsWithOutDataSubtemplateAttr(editor, evt);
        }
      },
      { priority: "highest" }
    );

    /**
     * Check if Ctrl + A is pressed or 'Select All' plugin is executed
     */
    let isSelectAll = false;
    const selectAll = this.editor.commands.get("selectAll");
    selectAll.on("execute", () => (isSelectAll = true));

    /**
    * Event listener after 'delete' and 'keydown' event is triggered
    */
    ["delete", "keydown"].forEach((event) => {
      this.listenTo(
        this.editor.editing.view.document,
        event,
        (evt, data) => {
          const isvalidAction = data && data.shiftKey;
          let key = null;
          if (data && data.domEvent && data.domEvent.key) {
            key = data.domEvent.key;
          }
          const model = this.editor.model;
          const selection = model.document.selection;
          const { anchor, focus } = selection;

          const anchorParent = anchor?.parent;
          const direction = data.direction || key;

          /**
           * Sets a editor selection focus one character backward because focus in next element start.
           * A shiftkey+right arrow select the entire line, i.e. all words that are in the line, as well as the focus moved to next line first character.
           * containsEntireContent - Checks whether the selection contains the entire content of the given element. 
           */
          if (selection.containsEntireContent(anchorParent) &&
            anchorParent !== focus?.parent &&
            focus.isAtStart) {
            model.modifySelection(selection, {
              direction: "backward",
              unit: "character",
            });
          }
          const rhpCitationRefClicked = JSON.parse(localStorage.getItem(RHP_CITATION_REF_CLICK));

          const isCopyAction = data?.domEvent && (data?.domEvent?.ctrlKey || data?.domEvent?.metaKey) &&
            (data?.domEvent?.key.toLowerCase() === 'c');

          const isCutAction = data?.domEvent && (data?.domEvent?.ctrlKey || data?.domEvent?.metaKey) &&
            (data?.domEvent?.key.toLowerCase() === 'x');

          const selectedBlocks = [...selection?.getSelectedBlocks()] || [];

          const isBulletOnlyCopy = selectedBlocks.length > 1 &&
            selectedBlocks.every(block => block.hasAttribute('listType'));

          if (
            (key === 'c' || key === 'x') &&
            (isCopyAction || isCutAction) &&
            isBulletOnlyCopy
          ) {
            if (isCutAction) {
              event.stop();
              data.preventDefault();
              const range = this.editor?.model?.document?.selection?.getFirstRange();
              range && this.editor?.plugins?.get(TRACK_CHANGES_EDITING) && this.editor?.plugins.get(TRACK_CHANGES_EDITING)?.markDeletion(range);
              // Copy the selection to clipboard using modern Clipboard API
              const selectedContent = this.editor.model.getSelectedContent(
                this.editor.model.document.selection
              );
              const htmlContent = this.editor.data.stringify(selectedContent);
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = htmlContent;
              const plainText = tempDiv.textContent || '';
              navigator.clipboard.write([
                new ClipboardItem({
                  'text/html': new Blob([htmlContent], { type: 'text/html' }),
                  'text/plain': new Blob([plainText], { type: 'text/plain' }),
                }),
              ])
            }
            this.editor.fire(SHOW_MESSAGE, { isBulletListCopyMessage: isCopyAction, isBulletListCutMessage: isCutAction });
          } else if (!isvalidAction &&
            !isSelectAll &&
            !rhpCitationRefClicked &&
            (this.isMultiLevelSelection() ||
              (anchorParent && this.deleteValidation(direction, anchorParent))) &&
            selection.getSelectedElement()?.name !== FIGURETAG
          ) {
            if (
              (isCopyAction || isCutAction) &&
              isBulletOnlyCopy
            ) {
              this.editor.fire(SHOW_MESSAGE, { isBulletListCopyMessage: isCopyAction, isBulletListCutMessage: isCutAction });
              return;
            }
            data.preventDefault();
            this.stopEventAndShowMessage(evt);
          }
          if (
            (direction === "forward" || direction === DELETE) &&
            anchor.isAtEnd &&
            anchorParent.name === SPAN &&
            !anchorParent.nextSibling
          ) {
            const ancestorsList = this.getModelReverseAncestors(this.editor);
            const lastSpan = ancestorsList.findLast((item) => {
              return item.name === SPAN;
            });
            const { data, model } = this.editor;
            const selectionInDoc = data.stringify(
              model.getSelectedContent(model.document.selection)
            );
            if (lastSpan && !lastSpan.nextSibling && !selectionInDoc) {
              model.change((writer) => {
                const paragraphPos = writer.createPositionAt(
                  lastSpan.parent,
                  "end"
                );
                writer.setSelection(paragraphPos);
              });
            }
          }
          isSelectAll = false;
        },
        {
          priority: "highest",
        }
      );
    });
    /**
     * Event listener after 'Enter' event is done
     */
    editor.commands
      .get("enter")
      .on("afterExecute", this.afterExecuteEnter.bind(this), {
        priority: "highest",
      });

    /**
     * Event listener on 'Backspace' event is triggered
     */
    editor.commands
      .get("delete")
      .on("execute", this.whenCursorAtStartOfHeading.bind(this), {
        priority: "highest",
      });

    /**
      * Event listener for the 'mouseup' event
      * This function checks if the anchor's parent is a linkspan and sets the selection to the link element if it is.
      */
    editor.editing.view.document.on(MOUSEUP, (evt, domEventData) => {
      const model = this.editor.model;
      const selection = model.document.selection;
      const { anchor, focus } = selection;

      if (!anchor || !focus) return;

      const isCursorOnly = selection.isCollapsed;
      const isRightClick = domEventData.domEvent.button === 2;
      const selectedBlocks = [...selection.getSelectedBlocks()];
      const isLinkSpan = anchor?.parent.name === LINK_SPAN || _.some(selectedBlocks, el => el.name === LINK_SPAN);


      // Move cursor outside linkspan (left-click only) to allow bullet list formatting
      if (!isRightClick && isCursorOnly && isLinkSpan && (anchor.isAtStart || anchor.isAtEnd)) {
        model.change(writer => {
          const position = anchor?.isAtStart
            ? writer.createPositionBefore(anchor?.parent)
            : writer.createPositionAfter(anchor?.parent);
          writer.setSelection(position);
        });
        return;
      }

      // If selection spans full block (even if ends in linkspan), expand to full block
      if (!isCursorOnly) {
        const findLinkSpan = (element) => _.find(element.getAncestors(), el => el.is('element') && model.schema.isBlock(el) && el.name !== LINK_SPAN);
        const anchorBlock = findLinkSpan(anchor);
        const focusBlock = findLinkSpan(focus);

        if (anchorBlock && focusBlock) {
          const isFromStart = selection.getFirstPosition().isEqual(model.createPositionAt(anchorBlock, 0));
          const isToEnd = selection.getLastPosition().isEqual(model.createPositionAt(focusBlock, 'end'));

          if (isFromStart && isToEnd) {
            model.change(writer => {
              const range = model.createRange(
                model.createPositionAt(anchorBlock, 0),
                model.createPositionAt(focusBlock, 'end')
              );
              writer.setSelection(range);
            });
            return;
          }
        }
      }

      // Handle explicit link-only selection
      const isOnlyLinkSelected = selectedBlocks.length === 1 && selectedBlocks[0].name === LINK_SPAN;
      // Collapse cursor into link span if only link is selected and collapsed
      if (isOnlyLinkSelected && isCursorOnly && !isRightClick && (anchor.isAtStart || anchor.isAtEnd)) {
        model.change(writer => {
          writer.setSelection(selectedBlocks[0], 'on');
        });
      }
      // Full selection of a link span with both anchor and focus inside the same link
      else if (!isCursorOnly && (anchor?.parent.name === LINK_SPAN) && (focus?.parent.name === LINK_SPAN) && _.every(selectedBlocks, el => el.name === LINK_SPAN) && !isRightClick
      ) {
        model.change(writer => {
          writer.setSelection(
            writer.createRange(
              writer.createPositionBefore(anchor.parent),
              writer.createPositionAfter(anchor.parent)
            )
          );
        });
      }

      // Adjust link selection range from start of link to current selection end
      else if (anchor?.parent.name === LINK_SPAN && anchor?.isAtStart && checkSelectedLink(this.editor)) {
        model.change(writer => {
          const linkBlock = _.find(selectedBlocks, el => el.name === LINK_SPAN);
          if (linkBlock) {
            const startPos = writer.createPositionAt(linkBlock, 'before');
            writer.setSelection(writer.createRange(startPos, selection.getLastPosition()));
          }
        });
      }
    });
  }

  /**
   * Returns boolean whether the deletion scenario to proceed further or restrict.
   * @param direction - Type of deletion;
   * @param anchorParent - Anchor parent element;
   * @return {Boolean}
   */
  deleteValidation(direction, anchorParent) {
    const viewAnchor = this.editor.editing.view.document.selection.anchor;
    const modelAnchor = this.editor.model.document.selection.anchor;
    let stopDeletion;

    if ((direction === "backward" || direction === BACKSPACE) && viewAnchor.isAtStart) {
      stopDeletion = this.backwardRestrictCheck(anchorParent);
    }
    if ((direction === "forward" || direction === DELETE) && modelAnchor.isAtEnd && modelAnchor.parent && modelAnchor.parent.name !== SPAN) {
      stopDeletion = this.forwardRestrictCheck(anchorParent);
    }
    return stopDeletion;
  }

  /**
   * Check whether the backspace deletion to proceed further or restrict.
   * @param {*} anchorParent - Anchor parent element;
   * @return {Boolean}
   */
  backwardRestrictCheck(anchorParent) {
    const {
      mapper,
      view: {
        document: {
          selection: {
            anchor: { parent },
          },
        },
      },
    } = this.editor.editing;

    const { selection: { anchor: { isAtStart } } } = this.editor.model.document;

    const {
      previousSibling,
      parent: { name },
    } = anchorParent;
    let isPreviousElementKeyword =
      parent &&
      parent.previousSibling &&
      parent.previousSibling._attrs &&
      !parent.previousSibling._textData &&
      parent.previousSibling.getAttribute(CLASS) === WK_KEYWORD_REF;
    return isPreviousElementKeyword
      ? false
      : (previousSibling &&
        isAtStart &&
        mapper.toViewElement(previousSibling) &&
        mapper.toViewElement(previousSibling)._attrs &&
        mapper
          .toViewElement(previousSibling)
          .hasAttribute(SUBTEMPLATE_NAME)) ||
      (anchorParent && isAtStart && name === DIV && !previousSibling);
  }

  /**
   * Check whether the forward deletion to proceed further or restrict.
   * @param {*} anchorParent - Anchor parent element;
   * @return {Boolean}
   */
  forwardRestrictCheck(anchorParent) {
    const { editing: { view: { document: { selection } }, mapper }, model } = this.editor;
    const selectionInDoc = this.editor.data.stringify(
      model.getSelectedContent(model.document.selection)
    );
    const modelIsBackward = this.editor.model.document.selection.isBackward;
    let isNextElementKeyword = false;
    if (selection && selection.anchor && selection.anchor.parent &&
      ((selection.anchor.parent.nextSibling && selection.anchor.parent.nextSibling._attrs &&
        selection.anchor.parent.nextSibling.getAttribute(CLASS) === WK_KEYWORD_REF) ||
        (selection.anchor.isAtEnd &&
          selection.anchor.parent.parent &&
          selection.anchor.parent.parent.getAttribute(CLASS) === WK_KEYWORD_REF))
    ) {
      isNextElementKeyword = true;
    }
    return isNextElementKeyword || selectionInDoc || modelIsBackward
      ? false
      : (anchorParent.nextSibling &&
        anchorParent.nextSibling._attrs &&
        mapper.toViewElement(anchorParent.nextSibling) &&
        mapper.toViewElement(anchorParent.nextSibling)._attrs
        ? mapper
          .toViewElement(anchorParent.nextSibling)
          .hasAttribute(SUBTEMPLATE_NAME)
        : false) ||
      !this.getModelReverseAncestors(this.editor).find((item) => {
        if (mapper.toViewElement(item).name === P) {
          if (item._attrs && item.hasAttribute("listStyle")) {
            if (item.nextSibling) {
              return item;
            }
          } else if (mapper.toViewElement(item).nextSibling) {
            return item;
          }
        }
      });
  }

  inlineSubTempOnLi(isViewSelectionHasSpan, editor) {
    editor.model.change((writer) => {
      const trackChangesMarkerType = this.returnTrackChangeType(editor);
      const selection = editor.model.document.selection;
      const firstPosition = selection.getFirstPosition();
      const { parent } = firstPosition;
      const spanParentElement = editor.editing.mapper.toModelElement(
        isViewSelectionHasSpan.parent
      );
      const SpanElement = editor.editing.mapper.toModelElement(
        isViewSelectionHasSpan
      );
      let movableRange = null;
      // Finds nearest li element parent from cursor position
      const filteredList = editor.model.document.selection.anchor.parent
        .getAncestors()
        .reverse()
        .find((item) => (item.name === PARAGRAPH && item.hasAttribute("listType")));
      const pos = selection.anchor.isAtStart
        ? writer.createPositionBefore(filteredList)
        : writer.createPositionAfter(filteredList);
      const newLi = writer.cloneElement(filteredList, false);
      writer.setAttribute("listItemId", randomUUIDGenerator(), newLi);
      writer.insert(newLi, pos);

      if (SpanElement.getChild(0) !== selection.anchor.parent) {
        let selectionParentEndPos = writer.createPositionAt(parent, "end");
        let parentEndPos = writer.createPositionAt(spanParentElement, "end");

        movableRange = writer.createRange(firstPosition, selectionParentEndPos);

        if (movableRange) {
          let posOfLiTag = writer.createPositionAt(newLi, 0);
          try {
            writer.move(movableRange, posOfLiTag);
            this.trackChangesForMovedSubtemplate(newLi, trackChangesMarkerType, writer);
            let posOfLiTagEnd = writer.createPositionAt(newLi, "end");
            writer.move(
              writer.createRange(
                writer.createPositionAt(SpanElement, "after"),
                parentEndPos,
                "end"
              ),
              posOfLiTagEnd
            );
            // Removes parent span tag in new p tag
            this.unwarpItem(newLi);
          } catch (err) {
            if (err) {
              console.warn(err);
            }
          }
        }
      }
      const liPos = writer.createPositionAt(newLi, 0);
      writer.setSelection(liPos);
    });
  }

  inlineSubTempOnP(isViewSelectionHasSpan, editor) {
    const selection = editor.model.document.selection;
    const firstPosition = selection.getFirstPosition();
    const { parent } = firstPosition;
    const spanParentElement = editor.editing.mapper.toModelElement(
      isViewSelectionHasSpan.parent
    );
    const SpanElement = editor.editing.mapper.toModelElement(
      isViewSelectionHasSpan
    );
    let movableRange = null;

    editor.model.change((writer) => {
      const posAfter = selection.anchor.isAtStart
        ? writer.createPositionBefore(spanParentElement)
        : writer.createPositionAfter(spanParentElement);

      const paragraph = writer.createElement(PARAGRAPH);
      writer.insert(paragraph, posAfter);
      const trackChangesMarkerType = this.returnTrackChangeType(editor);

      if (SpanElement.getChild(0) !== selection.anchor.parent) {
        let selectionParentEndPos = writer.createPositionAt(parent, "end");
        let parentEndPos = writer.createPositionAt(spanParentElement, "end");

        if (selection.anchor.parent.name !== "paragraph") {
          movableRange = writer.createRange(
            firstPosition,
            selectionParentEndPos
          );
        }

        if (movableRange) {
          let posOfPTag = writer.createPositionAt(paragraph, 0);
          try {
            writer.move(movableRange, posOfPTag);
            this.trackChangesForMovedSubtemplate(paragraph, trackChangesMarkerType, writer);
            let posOfPTagEnd = writer.createPositionAt(paragraph, "end");
            writer.move(
              writer.createRange(
                writer.createPositionAt(SpanElement, "after"),
                parentEndPos,
                "end"
              ),
              posOfPTagEnd
            );
            // Removes parent span tag in new p tag
            this.unwarpItem(paragraph);
          } catch (err) {
            if (err) {
              console.warn(err);
            }
          }
        }
      }
      // Moves cursor to new p tag created
      const paragraphPos = writer.createPositionAt(paragraph, 0);
      writer.setSelection(paragraphPos);
    });
  }

  trackChangesForMovedSubtemplate(item, trackChangesMarkerType, writer) {
    this.createTrackChangeSuggestion(
      trackChangesMarkerType,
      writer.createRangeIn(item),
      this.editor
    )
  }

  unwarpItem(element) {
    this.editor.model.change((writer) => {
      [...element.getChildren()].forEach((item) => {
        item.name === SPAN && writer.unwrap(item)
      });
    });
  }

  // Restrict enter in green text
  spanTagsWithOutDataSubtemplateAttr(editor, evt) {
    editor.model.change((writer) => {
      let firstblockEle = this.getReverseAncestors(editor).find(
        (item) => item.name === P
      );
      if (firstblockEle) {
        evt.stop();
        const trackChangesMarkerType = this.returnTrackChangeType(editor);
        const selection = editor.model.document.selection;
        let split;
        // If condition to check of cursor is at end of the block element tag and no siblings after cursor
        if (
          !selection.anchor.isAtEnd &&
          !selection.anchor.isAtStart &&
          selection.anchor.parent &&
          !selection.anchor.parent.nextSibling
        ) {
          split = writer.split(selection.getFirstPosition());
        }

        const firstblockModelEle =
          editor.editing.mapper.toModelElement(firstblockEle);
        const posAfter = selection.anchor.isAtStart
          ? writer.createPositionBefore(firstblockModelEle)
          : writer.createPositionAfter(firstblockModelEle);
        const paragraph = writer.createElement(PARAGRAPH);
        writer.insert(paragraph, posAfter);
        this.appendNextEleOnEnterKey(split, paragraph, trackChangesMarkerType);
      }
    });
  }

  // Restrict text on new line if enter is on IST and green text
  appendNextEleOnEnterKey(split, nextEle, trackChangesMarkerType) {
    this.editor.model.change((writer) => {
      if (split) {
        [...split.range.getItems()].forEach((item) => {
          writer.insert(item, nextEle);
        });
      }

      // Removes parent span tag in new p tag
      [...nextEle.getChildren()].forEach((item) => {
        if (item.name === SPAN) {
          writer.unwrap(item);
        }
      });

      // Moves cursor to new p tag created
      const paragraphPos = writer.createPositionAt(nextEle, 0);
      writer.setSelection(paragraphPos);

      // Applies trackchange in new p tag created
      const rangeInP = writer.createRangeIn(nextEle);
      this.createTrackChangeSuggestion(
        trackChangesMarkerType,
        rangeInP,
        this.editor
      );
    });
  }

  createTrackChangeSuggestion(trackChangesMarkerType, range, editor) {
    if (
      editor.commands.get("trackChanges").value &&
      editor.plugins.get("TrackChanges").isEnabled &&
      trackChangesMarkerType
    ) {
      if (trackChangesMarkerType === INSERTION) {
        editor.plugins.get(TRACK_CHANGES_EDITING).markInsertion(range);
      }
      if (trackChangesMarkerType === FORMAT_INLINE) {
        editor.plugins.get(TRACK_CHANGES_EDITING).markInlineFormat(range);
      }
      if (trackChangesMarkerType === DELETION) {
        editor.plugins.get(TRACK_CHANGES_EDITING).markDeletion(range);
      }
    }
  }

  /**
   *
   * @param {*} editor editor instance
   * @returns trackchangestype on current cursor position
   */
  returnTrackChangeType(editor) {
    let trackChangesMarkerType = null;
    for (const marker of [
      ...editor.model.markers.getMarkersAtPosition(
        editor.model.document.selection.getFirstPosition()
      ),
    ]) {
      if (marker.name.includes("suggestion")) {
        trackChangesMarkerType = marker.name.split(":")[1];
      }
    }
    return trackChangesMarkerType;
  }

  /**
   * Check parent is header & subtempate
   *
   * @param {*} ancestors
   * @param {number} [idx=0]
   * @param {boolean} enterKeyPressed
   * @returns {boolean}
   */
  checkParentElementHeader(ancestors, idx = 0, enterKeyPressed = false) {
    const node = ancestors[idx];
    if (
      node.name &&
      (HEADING_TAG_NAMES.includes(node.name) &&
        (node.getAttribute(PROPERTY) === DCTERMS_TITLE ||
          (node.parent && node.parent.name == HEADER)) ||
        (enterKeyPressed &&
          node.hasAttribute(CLASS) &&
          node.getAttribute(CLASS).includes(WK_KEYWORD_REF)))
    ) {
      return true;
    }
    return node.parent
      ? this.checkParentElementHeader(ancestors, ++idx, enterKeyPressed)
      : false;
  }

  /**
   * Disable backspace when user click at start of header
   * @date 10/9/2023 - 12:15:29 PM
   *
   * @param {*} evt
   * @param {*} _
   * @returns {*} void 0
   */
  whenCursorAtStartOfHeading(evt, _) {
    const ancestors = this.getReverseAncestors(this.editor);
    if (
      this.editor.model.document.selection.anchor.isAtStart &&
      this.editor.model.document.selection.anchor.parent.getAttribute(
        "class"
      ) !== WK_KEYWORD_REF &&
      this.checkParentElementHeader(ancestors)
    ) {
      return this.stopEventAndShowMessage(evt);
    }
  }

  /**
   * use to get reverse Ancestors from editing
   * @param {*} editor
   * @returns {*}
   */
  getReverseAncestors(editor) {
    return editor.editing.view.document.selection.anchor
      .getAncestors()
      .reverse();
  }

  /**
   * use to get reverse Ancestors from model
   * @param {*} editor
   * @returns {*}
   */
  getModelReverseAncestors(editor) {
    return editor.model.document.selection.anchor.getAncestors().reverse();
  }

  /**
   * Check last child is part of list
   *
   * @param {*} ancestors
   * @param {number} [idx=0]
   * @returns {*}
   */
  checkPartOfList(ancestors, idx = 0) {
    const node = ancestors[idx];
    if (
      node.name &&
      (node.hasAttribute(LIST_STYLE) || node.name === LIST_ITEM)
    ) {
      return true;
    }
    return node.parent ? this.checkPartOfList(ancestors, ++idx) : false;
  }

  /**
   * Prevent copy attributes when not part of list
   *
   * @date 10/10/2023 - 2:47:14 PM
   */
  afterExecuteEnter() {
    const { model } = this.editor;
    const ancestors = this.getReverseAncestors(this.editor);
    const isList = this.checkPartOfList(ancestors, 0);
    if (!isList) {
      const block = first(model.document.selection.getSelectedBlocks());
      model.change((writer) => {
        /* Remove inherited attributes from newly created elements */
        Array.from(block.getAttributes()).forEach((item) => {
          writer.removeAttribute(item[0], block);
        });
      });
    }
  }

  /**
   * @returns true if selection start and end inside different parent
   * @returns false if selection start and ends under same parent
   */
  isMultiLevelSelection() {
    const selection = this.editor.model.document.selection;
    const selectedBlocks = [...selection.getSelectedBlocks()];
    const { anchor, focus } = selection;
    const isLinkSpanOrSpan = node => node.name === LINK_SPAN || node.name === SPAN;
    try {
      return (
        (selectedBlocks.length > 1 &&
          !selectedBlocks.some(item => item && (item.name === FIGURETAG || item.name === LINK_SPAN)))
        ||
        (anchor.parent !== focus.parent && !isLinkSpanOrSpan(anchor.parent) && !isLinkSpanOrSpan(focus.parent))
      );
    } catch {
      return false;
    }
  }

  /**
   * Stop event & show message
   */
  stopEventAndShowMessage(evt) {
    // isDeletingReferenceElement will be true if citation reference or editorial notes/release notes is present at start of editable tag
    // If true existing citation or editorial notes/release notes delete scenario will be triggered
    // If false further deletion of 'P' tag will be blocked with a notification
    let isDeletingReferenceElement;

    try {
      const selection = this.editor.model.document.selection;
      isDeletingReferenceElement = [NUMBERED_CITATION, RELEASE_NOTES].includes(selection?.getSelectedElement?.()?.name);
    } catch {
      isDeletingReferenceElement = false;
    }

    if (!isDeletingReferenceElement) {
      evt.stop();
      this.editor.fire(SHOW_MESSAGE);
      return void 0;
    }
  }
}
